import React, {
  useCallback, useMemo, useRef, useState,
} from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  ArrowLeft, Download, Upload, CheckCircle, RotateCcw,
} from 'lucide-react';

import Button from '../../components/ui/Button';
import IconButton from '../../components/ui/IconButton';
import Tooltip from '../../components/ui/Tooltip';
import {
  Container,
  StickyHeader,
  Title,
  Card,
  DropZone,
  DropText,
  FileName,
  SummaryGrid,
  SummaryCard,
  SummaryValue,
  SummaryLabel,
  Footer,
  HistoryTable,
  SectionTitle,
} from '../../components/import/ImportStyles';
import {
  getExtraBalanceTemplate,
  validateExtraBalanceImport,
  executeExtraBalanceImport,
} from '../../services/leaveExtraBalanceService';
import { formatEffectiveDate } from './config';

// Ek izin — Excel ile toplu yükleme. Akış devir ekranıyla (LeaveOpeningBalancePage)
// aynı: şablon indir → dosya seç (otomatik doğrulanır) → önizleme → kaydet.
//
// KRİTİK: execute'un DÖNDÜĞÜ satırlar gösterilir, gönderilenler değil — önizlemede
// geçerli görünen bir satır kayıtta reddedilmiş olabilir (ör. bu arada biri aynı ek
// izni elle girdiyse "zaten var").
//
// ÖNİZLEME TABLOSU SINIRLI: dosya 10.000 satıra kadar olabilir; tablo hatalı satırları
// ÖNCE ve en çok PREVIEW_LIMIT satır çizer (on bin satırlık bir DOM tabloyu
// kilitlerdi). Sayılar her zaman sunucunun tüm dosya için verdiği özettir.
export const PREVIEW_LIMIT = 300;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  h1 { margin-bottom: 0; }
`;

const Hint = styled.p`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: #667085;
  line-height: 1.6;
  margin: 0;
`;

const Truncated = styled.p`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #667085;
  margin: 8px 0 0;
`;

function translateRowError(t, key) {
  return t(`responseErrors.${key}`, { defaultValue: t(key, { defaultValue: key }) });
}

// Hatalı satırlar önce, sonra dosya sırası.
export function previewRows(rows, limit = PREVIEW_LIMIT) {
  const sirali = [...(rows || [])].sort((a, b) => {
    if (a.isValid !== b.isValid) return a.isValid ? 1 : -1;
    return a.rowNumber - b.rowNumber;
  });
  return sirali.slice(0, limit);
}

export default function LeaveExtraBalanceImportPage() {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  const fileInputRef = useRef(null);

  const rows = useMemo(() => result?.rows ?? [], [result]);
  const shown = useMemo(() => previewRows(rows), [rows]);
  const validRows = result?.validRows ?? 0;

  const goBack = useCallback(() => history.push('/leave-extra-balance'), [history]);

  const handleDownloadTemplate = useCallback(async () => {
    const res = await getExtraBalanceTemplate();
    // 403/hata durumunda res.data gerçek bir Blob DEĞİL, EMPTY_RESPONSE gövdesidir —
    // denetlemeden indirilirse tarayıcı birkaç baytlık bozuk bir .xlsx kaydeder.
    if (!(res?.data instanceof Blob) || res.data.size < 100) return;
    const url = window.URL.createObjectURL(res.data);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'flexbee-ek-izin-sablonu.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }, []);

  const handleFileChosen = useCallback(async (file) => {
    if (!file || !file.name.toLowerCase().endsWith('.xlsx')) return;
    setFileName(file.name);
    setSaved(null);
    setResult(null);
    setBusy(true);
    try {
      const res = await validateExtraBalanceImport(file);
      if (res?.data?.status) setResult(res.data.data);
    } catch {
      // Hata toast'ı interceptor'da; finally busy'yi geri açsın diye yakalanır.
    } finally {
      setBusy(false);
    }
  }, []);

  const handleInputChange = useCallback((e) => {
    handleFileChosen(e.target.files?.[0]);
    e.target.value = ''; // aynı dosya tekrar seçilebilsin
  }, [handleFileChosen]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (busy) return;
    handleFileChosen(e.dataTransfer.files?.[0]);
  }, [handleFileChosen, busy]);

  const handleSave = useCallback(async () => {
    setBusy(true);
    try {
      const res = await executeExtraBalanceImport(rows);
      if (res?.data?.status) {
        setResult(res.data.data);
        setSaved(res.data.data.validRows ?? 0);
      }
    } catch {
      // Hata toast'ı interceptor'da.
    } finally {
      setBusy(false);
    }
  }, [rows]);

  const handleReset = useCallback(() => {
    setResult(null);
    setFileName('');
    setSaved(null);
  }, []);

  return (
    <Container>
      <StickyHeader>
        <Header>
          <Tooltip text={t('leaveExtraBalance.backToList')}>
            <IconButton size="sm" aria-label={t('leaveExtraBalance.backToList')} onClick={goBack}>
              <ArrowLeft size={16} />
            </IconButton>
          </Tooltip>
          <Title>{t('leaveExtraBalance.importTitle')}</Title>
        </Header>
      </StickyHeader>

      <Card>
        <SectionTitle>{t('leaveExtraBalance.howItWorks')}</SectionTitle>
        <Hint>{t('leaveExtraBalance.importDescription')}</Hint>
        <Footer>
          <Button variant="outline" onClick={handleDownloadTemplate}>
            <Download size={16} style={{ marginRight: 8 }} />
            {t('leaveExtraBalance.downloadTemplate')}
          </Button>
          <div />
        </Footer>
      </Card>

      <Card>
        <SectionTitle>{t('leaveExtraBalance.uploadFile')}</SectionTitle>

        <DropZone
          $dragOver={dragOver}
          onClick={() => { if (!busy) fileInputRef.current?.click(); }}
          onDragOver={(e) => { e.preventDefault(); if (!busy) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          style={busy ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
        >
          <Upload size={32} color="#667085" />
          <DropText>
            {fileName ? <FileName>{fileName}</FileName> : t('leaveExtraBalance.dropzone')}
          </DropText>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            data-testid="extra-balance-file"
            disabled={busy}
            style={{ display: 'none' }}
            onChange={handleInputChange}
          />
        </DropZone>

        {result && (
          <>
            <SummaryGrid style={{ marginTop: 20 }}>
              <SummaryCard $color="#F0FDF4">
                <SummaryValue data-testid="extra-balance-valid">{validRows}</SummaryValue>
                <SummaryLabel>{t('leaveExtraBalance.validRows')}</SummaryLabel>
              </SummaryCard>
              <SummaryCard $color="#EFF6FF">
                <SummaryValue data-testid="extra-balance-total-days">{result.totalDays ?? 0}</SummaryValue>
                <SummaryLabel>{t('leaveExtraBalance.totalDays')}</SummaryLabel>
              </SummaryCard>
              <SummaryCard $color="#FEF2F2">
                <SummaryValue data-testid="extra-balance-errors">{result.errorRows ?? 0}</SummaryValue>
                <SummaryLabel>{t('leaveExtraBalance.errorRows')}</SummaryLabel>
              </SummaryCard>
            </SummaryGrid>

            <HistoryTable>
              <thead>
                <tr>
                  <th>{t('leaveExtraBalance.rowNumber')}</th>
                  <th>{t('leaveExtraBalance.registrationNumber')}</th>
                  <th>{t('leaveExtraBalance.fullName')}</th>
                  <th>{t('leaveExtraBalance.days')}</th>
                  <th>{t('leaveExtraBalance.effectiveDate')}</th>
                  <th>{t('leaveExtraBalance.note')}</th>
                  <th>{t('leaveExtraBalance.variation')}</th>
                  <th>{t('leaveExtraBalance.rowError')}</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr
                    key={r.rowNumber}
                    data-testid={`extra-balance-row-${r.rowNumber}`}
                    style={!r.isValid ? { background: '#FEF2F2' } : undefined}
                  >
                    <td>{r.rowNumber}</td>
                    <td>{r.registrationNumber}</td>
                    <td>{r.fullName}</td>
                    <td>{r.days ?? '-'}</td>
                    <td>{formatEffectiveDate(r.effectiveDate, i18n.language)}</td>
                    <td>{r.note}</td>
                    <td>{r.leaveVariationName || '-'}</td>
                    <td>{r.errorKey ? translateRowError(t, r.errorKey) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </HistoryTable>
            {rows.length > shown.length && (
              <Truncated data-testid="extra-balance-truncated">
                {t('leaveExtraBalance.previewTruncated', { shown: shown.length, total: rows.length })}
              </Truncated>
            )}
          </>
        )}

        <Footer>
          <div>
            {saved !== null && (
              <Tooltip text={t('leaveExtraBalance.resetTooltip')}>
                <IconButton onClick={handleReset} aria-label={t('leaveExtraBalance.resetTooltip')}>
                  <RotateCcw size={16} />
                </IconButton>
              </Tooltip>
            )}
          </div>
          {saved === null && (
            <Button
              data-testid="extra-balance-submit"
              disabled={validRows === 0 || busy}
              onClick={handleSave}
            >
              <CheckCircle size={16} style={{ marginRight: 8 }} />
              {t('leaveExtraBalance.saveRows', { count: validRows })}
            </Button>
          )}
        </Footer>

        {saved !== null && (
          <Hint data-testid="extra-balance-saved">
            <CheckCircle size={14} color="#16a34a" style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />
            {t('leaveExtraBalance.savedSummary', { count: saved })}
            {' '}
            <Button variant="ghost" onClick={goBack}>{t('leaveExtraBalance.backToList')}</Button>
          </Hint>
        )}
      </Card>
    </Container>
  );
}
